import type { GeoJSONFeature, GeoJSONFeatureCollection } from '../types/gis';

/** Normalize a Feature OR FeatureCollection (or anything malformed) into a
 *  flat features list. Single-geometry GIS tools like `geojson_convex_hull`
 *  return a Feature, not an FC — without this guard, anything that calls
 *  `for (const f of fc.features)` blows up on a non-iterable. */
function getFeatures(input: any): GeoJSONFeature[] {
  if (!input || typeof input !== 'object') return [];
  if (input.type === 'Feature') return [input as GeoJSONFeature];
  if (Array.isArray(input.features)) return input.features as GeoJSONFeature[];
  return [];
}

type FcOrFeature = GeoJSONFeatureCollection | GeoJSONFeature | null | undefined;

/** Group features by their properties.layer string. Features without
 *  properties.layer go into the 'default' bucket. The layer-toggle UI
 *  is only rendered when MORE than one bucket exists. Accepts a Feature
 *  OR FeatureCollection. */
export function featureCollectionToLayers(
  fc: FcOrFeature,
): Record<string, GeoJSONFeature[]> {
  const buckets: Record<string, GeoJSONFeature[]> = {};
  for (const feat of getFeatures(fc)) {
    const layer = (feat?.properties && (feat.properties as any).layer) || 'default';
    if (!buckets[layer]) buckets[layer] = [];
    buckets[layer].push(feat);
  }
  return buckets;
}

/** True if the collection has more than one named layer (drives layer-toggle UI).
 *  Returns false for a single Feature, an empty FC, or any non-collection input. */
export function hasMultipleLayers(fc: FcOrFeature): boolean {
  const names = new Set<string>();
  for (const feat of getFeatures(fc)) {
    const layer = (feat?.properties && (feat.properties as any).layer) || 'default';
    names.add(layer);
    if (names.size > 1) return true;
  }
  return false;
}
